/* import __COLOCATED_TEMPLATE__ from './guidance-editor.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import { inject as service } from '@ember/service';
// @ts-ignore
import { BaseConfig, BlocksDocument, EMOJI_TYPEAHEAD } from '@intercom/composer-ember';
import type { Router } from '@ember/routing';
import type AiAgentGuidelineService from 'embercom/services/ai-agent-guideline-service';
import type Store from '@ember-data/store';
import type AppService from 'embercom/services/app-service';
import { put } from 'embercom/lib/ajax';

class SimpleComposerConfig extends BaseConfig {
  autoFocus = true;
  allowedBlocks = ['paragraph'];
  allowedInline = ['anchor'];
  allowedMarks = ['anchor'];
  maxCharacterCount = 2500;
  placeholder: string;
  tools: Array<{ name: string }> = [];
  experimental = {
    hideInsertersOnMouseOut: true,
  };
  typeaheads = [EMOJI_TYPEAHEAD];

  constructor({ placeholder }: { placeholder: string }) {
    super();
    this.placeholder = placeholder;
  }
}

interface Signature {
  Args: {
    initialText?: string;
    placeholder?: string;
    actionName?: string;
    onCancel: () => void;
    editingGuidance?: boolean;
  };
}

export default class GuidanceEditor extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare router: Router;
  @service declare store: Store;
  @service declare aiAgentGuidelineService: AiAgentGuidelineService;
  @service declare appService: AppService;
  @service declare notificationsService: any;

  @tracked guidanceText = '';
  @tracked composerApi: any = null;
  @tracked blocksDoc: BlocksDocument;

  constructor(owner: unknown, args: never) {
    super(owner, args);
    this.guidanceText = this.args.initialText || '';
    this.blocksDoc = new BlocksDocument(
      this.guidanceText
        .split('\n')
        .map((line) => ({ type: 'paragraph', text: line, class: 'no-margin' })) || [],
    );
  }

  @action
  onGuidanceChange(blocksDoc: BlocksDocument) {
    this.guidanceText = blocksDoc.blocks
      .map((block: any) => block.text)
      .join('\n')
      .trim();
  }

  @action
  onComposerReady(composerApi: any) {
    this.composerApi = composerApi;
  }

  @action
  async enableGuidance() {
    let guideline = await this.createGuideline();

    // TODO make this into a task and have a button loading indicator in the UI
    try {
      let response = await put(`/ember/ai_agent/guidelines/${guideline.id}/enable`, {
        id: guideline.id,
        app_id: this.appService.app.id,
      });
      if (response) {
        guideline.reload();
      }
      this.notificationsService.notifyConfirmation(
        this.intl.t('automation.analyze.task-suggestions.guidance-set-live'),
      );
    } catch (error) {
      this.notificationsService.notifyResponseError(error);
      console.error('Failed to enable guideline:', error);
    }
    // TODO: save the guideline ID in the suggestion metadata so we link to it from the task suggestion
    // Then reload / update the UI to show the guideline as live
  }

  @action
  async editGuidance() {
    // Create a new guideline with the current guidance text
    try {
      let newGuideline = await this.createGuideline();
      this.notificationsService.notifyConfirmation(
        this.intl.t('automation.analyze.task-suggestions.guidance-created-successfully'),
      );

      // Set the newly created guideline as the active one
      this.aiAgentGuidelineService.setOpenGuidelineId(newGuideline.id);

      // TODO: save the guideline ID in the task suggestion metadata so we link to it from the task suggestion

      // Transition to the guidance page based on feature flag
      this.router.transitionTo(this.route, {
        queryParams: this.queryParams,
      });
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('automation.analyze.task-suggestions.failed-to-create-guidance'),
      );
      console.error('Failed to create guideline:', error);
    }
  }

  @action async createGuideline() {
    let guidelineTitle = this.args.actionName
      ? this.intl.t('automation.analyze.task-suggestions.escalation-guidance-for', {
          actionName: this.args.actionName,
        })
      : this.intl.t('automation.analyze.task-suggestions.task-suggestion-escalation-guidance');

    let newGuideline = await this.aiAgentGuidelineService.createNewGuideline('routing', {
      title: guidelineTitle,
      prompt: this.guidanceText,
    });

    return newGuideline;
  }

  get route() {
    return this.appService.app.canUseFeature('fin-escalation-rules')
      ? 'apps.app.automation.fin-ai-agent.escalation'
      : 'apps.app.automation.fin-ai-agent.guidance';
  }

  get queryParams() {
    return {};
  }

  @action
  handleCancel() {
    this.args.onCancel();
  }

  get placeholderText() {
    return this.intl.t('automation.analyze.task-suggestions.guidance-placeholder');
  }

  get composerConfig() {
    return new SimpleComposerConfig({
      placeholder: this.placeholderText,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TaskSuggestions::GuidanceEditor': typeof GuidanceEditor;
    'reporting/automation/fin-ai-agent/analyze/task-suggestions/guidance-editor': typeof GuidanceEditor;
  }
}
